/**
 * Documents banner provider behavior for ERP Accelerator.
 */

"use client";

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { X } from "lucide-react";
import CheckCircle from "lucide-react/dist/esm/icons/check-circle";
import XCircle from "lucide-react/dist/esm/icons/x-circle";
import AlertTriangle from "lucide-react/dist/esm/icons/alert-triangle";
import Info from "lucide-react/dist/esm/icons/info";

/**
 * Describes banner type data.
 */
export type BannerType = "info" | "success" | "warning" | "error";

/**
 * Describes banner options data.
 */
export interface BannerOptions {
  id?: string;
  type?: BannerType;
  title?: string;
  message: string;
  duration?: number; // ms
}

interface Banner extends Required<Omit<BannerOptions, "id" | "type" | "duration">> {
  id: string;
  type: BannerType;
  duration: number;
}

interface BannerContextValue {
  showBanner: (opts: BannerOptions) => string; // returns id
  dismissBanner: (id: string) => void;
}

const BannerContext = createContext<BannerContextValue | null>(null);

/**
 * Handles use banner behavior.
 */
export const useBanner = () => {
  const ctx = useContext(BannerContext);
  if (!ctx) throw new Error("useBanner must be used within BannerProvider");
  return ctx;
};

function getIcon(type: BannerType) {
  switch (type) {
    case "success":
      return <CheckCircle className="h-4 w-4 text-gray-900 flex-shrink-0" />;
    case "error":
      return <XCircle className="h-4 w-4 text-gray-900 flex-shrink-0" />;
    case "warning":
      return <AlertTriangle className="h-4 w-4 text-gray-900 flex-shrink-0" />;
    default:
      return <Info className="h-4 w-4 text-gray-900 flex-shrink-0" />;
  }
}

/**
 * Handles banner provider behavior.
 */
export const BannerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [banners, setBanners] = useState<Banner[]>([]);

  const dismissBanner = useCallback((id: string) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const showBanner = useCallback((opts: BannerOptions) => {
    const id = opts.id || Math.random().toString(36).slice(2);
    const type: BannerType = opts.type || "info";
    const duration = Math.max(1500, opts.duration ?? 4000);
    const title = opts.title ?? (type === "error" ? "Error" : type === "warning" ? "Warning" : type === "success" ? "Success" : "Info");
    const banner: Banner = { id, type, duration, title, message: opts.message };
    setBanners((prev) => [...prev, banner]);
    window.setTimeout(() => dismissBanner(id), duration);
    return id;
  }, [dismissBanner]);

  const value = useMemo(() => ({ showBanner, dismissBanner }), [showBanner, dismissBanner]);

  return (
    <BannerContext.Provider value={value}>
      {children}
      {/* Banner stack — top-right, black & white */}
      <div className="fixed top-4 right-4 z-[2000] space-y-2 w-96 max-w-[calc(100vw-2rem)]">
        {banners.map((b) => (
          <div
            key={b.id}
            className="bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden"
          >
            <div className="px-4 py-3 flex items-start gap-3">
              <div className="mt-0.5">{getIcon(b.type)}</div>
              <div className="flex-1 min-w-0">
                {b.title && (
                  <div className="text-sm font-semibold text-gray-900 leading-snug">{b.title}</div>
                )}
                <div className="text-sm text-gray-700 mt-0.5 break-words leading-snug">{b.message}</div>
              </div>
              <button
                aria-label="Dismiss"
                onClick={() => dismissBanner(b.id)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ProgressBar duration={b.duration} />
          </div>
        ))}
      </div>
    </BannerContext.Provider>
  );
};

const ProgressBar: React.FC<{ duration: number }> = ({ duration }) => {
  return (
    <div className="h-0.5 w-full bg-gray-100">
      <div
        className="h-0.5 bg-gray-900"
        style={{
          width: "100%",
          animation: `banner-shrink ${duration}ms linear forwards`,
        }}
      />
      <style>{`
        @keyframes banner-shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};
